import {
    AccessBayScheduling,
    AccessHaulerManagement,
    AccessPlantManagement,
    AccessProducerManagement,
    CanImpersonateUsers,
    Dashboard,
    GetMilkScheduleDetails,
    ListAdmins,
    ListCIPWash,
    ListDrivers,
    ListDropoffs,
    ListHaulingCompanies,
    ListInvoicing,
    ListLabReports,
    ListManifests,
    ListMonthlyReports,
    ListPickups,
    ListPickupsAndLabs,
    ListProcessors,
    ListProducers,
    ListReceivers,
    ListRouteSessions,
    ListScheduleRoutes,
    ListSharedFiles,
    ListSilos,
    ListSubAdmins,
    ListSubHauling,
    ListSubProcessors,
    ListSubProducers,
    ListTrailers,
    ListUserGroups,
    ListUserGroupsV2,
    ManageAssignees,
    ManageClosePeriod,
    ManageCustomFields,
    ManageDeferrals,
    ManageDeliveryReports,
    ManageHaulerAssignments,
    ManageHaulerPayReports,
    ManageHaulerRates,
    ManageHaulingCharges,
    ManageHaulingContracts,
    ManageLinkedUsers,
    ManageMilkPrices,
    ManagePaymentStatus,
    ManagePoolingDecisions,
    ManageProcessorTotals,
    ManageProducerAssignments,
    ManageProducerPayReports,
    ManageProducerTouchBases,
    ManageSplitProducers,
    ManageVendorPayReports,
    ProducerHaulingContractsAndCharges,
    UploadDownload,
    ViewAccountingReports,
    ViewDocumentation,
    ViewDriverLocations,
    ViewReceivingDropoffs,
    ViewMilkPredictionReport,
    ViewContracts,
    ViewContractPerformanceReport,
    ViewSupplyOutlookReport,
} from './permissions';
import LABELS from './constants';

const region = 'TCJ';

const SubAdminManagement = {
    label: LABELS.Management,
    permissions: [
        ListAdmins,
        ListProducers,
        ListSubProducers,
        ListDrivers,
        ListProcessors(region),
        ListSubProcessors(region),
        ListHaulingCompanies(region),
        ListSubHauling(region),
        ListTrailers,
        ListSubAdmins,
        ManageLinkedUsers,
        CanImpersonateUsers,
        // force wrap
    ],
};

const SubAdminGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard,
        ListSilos,
        ListUserGroups,
        ListUserGroupsV2,
        ViewDriverLocations,
        ListScheduleRoutes,
        ManageClosePeriod,
        ViewDocumentation,
        ListPickups(),
        ListDropoffs(),
        ListInvoicing,
        ListCIPWash,
        ListLabReports(),
        UploadDownload,
        ListRouteSessions(region),
        ManageDeliveryReports,
        ListManifests,
        GetMilkScheduleDetails(),
        ViewReceivingDropoffs,
        ListSharedFiles,
        AccessPlantManagement,
        AccessHaulerManagement,
        AccessProducerManagement,
        AccessBayScheduling,
        ViewContracts,
        ManageCustomFields,
        ViewMilkPredictionReport,
        ViewContractPerformanceReport,
        ViewSupplyOutlookReport,
    ],
};

const SubAdminPooling = {
    label: LABELS.Pooling,
    permissions: [
        ManagePoolingDecisions,
        ManageProcessorTotals,
        ManageProducerTouchBases,
        // force wrap
    ],
};

const SubAdminAccounting = {
    label: LABELS.Accounting,
    permissions: [
        ViewAccountingReports,
        ManageProducerAssignments,
        ManageHaulerAssignments,
        ManageAssignees,
        ManageMilkPrices,
        ManageDeferrals,
        ManageSplitProducers,
        ManageHaulingCharges(),
        ManageHaulingContracts(),
        ManageHaulerRates,
        ManageProducerPayReports,
        ManageHaulerPayReports,
        ManageVendorPayReports,
        ManagePaymentStatus,
        // force wrap
    ],
};

const SubProducerManagement = {
    label: LABELS.Management,
    permissions: [ListSubProducers],
};

const SubProducerGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard,
        ListPickups('View', ''),
        ListPickupsAndLabs,
        ListLabReports('View', ''),
        ListSharedFiles,
        AccessProducerManagement,
        // force wrap
    ],
};

const SubProducerAccounting = {
    label: LABELS.Accounting,
    permissions: [ListMonthlyReports(region)],
};

const SubProcessorManagement = {
    label: LABELS.Management,
    permissions: [ListReceivers, ListSubProcessors()],
};

const SubProcessorGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard,
        ListSilos,
        ListDropoffs('View', ''),
        ListInvoicing,
        ListRouteSessions(region, 'View', ''),
        ListManifests,
        GetMilkScheduleDetails('Manage', '(View, Create Demand)'),
        ListSharedFiles,
        ViewReceivingDropoffs,
        AccessPlantManagement,
        AccessBayScheduling,
        // force wrap
    ],
};

const SubHaulingManagement = {
    label: LABELS.Management,
    permissions: [ListDrivers, ListTrailers, ListSubHauling()],
};

const SubHaulingGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard,
        ViewDriverLocations,
        ListPickups(),
        ListDropoffs(),
        ListCIPWash,
        GetMilkScheduleDetails('View', ''),
        ViewReceivingDropoffs,
        ListSharedFiles,
        AccessHaulerManagement,
        // force wrap
    ],
};

const SubHaulingAccounting = {
    label: LABELS.Accounting,
    permissions: [ProducerHaulingContractsAndCharges],
};

const SubAdmin = [SubAdminManagement, SubAdminGeneral, SubAdminAccounting, SubAdminPooling];
const SubProducer = [SubProducerManagement, SubProducerGeneral, SubProducerAccounting];
const SubProcessor = [SubProcessorManagement, SubProcessorGeneral];
const SubHauling = [SubHaulingManagement, SubHaulingGeneral, SubHaulingAccounting];

const REQUIRED_PERMISSIONS = {
    Admin: [
        ListSilos,
        ListUserGroups,
        ViewReceivingDropoffs,
        ListInvoicing,
        ListManifests,
        GetMilkScheduleDetails(),
        ListTrailers,
        // force-wrap
    ],
};

const TCJ = {
    Admin: { permissions: SubAdmin, required: REQUIRED_PERMISSIONS.Admin },
    'Sub-Admin': { permissions: SubAdmin },
    'Sub-Producer': { permissions: SubProducer },
    Accountant: { permissions: SubProducer },
    'Feed-Provider': { permissions: SubProducer },
    Nutritionist: { permissions: SubProducer },
    'Co-Owner': { permissions: SubProducer },
    'Sub-Processor': { permissions: SubProcessor },
    'Sub-Hauling': { permissions: SubHauling },
};

export default TCJ;
